/**
 * Afrikaans Word Bank Manager
 * Handles loading, processing, and accessing the Afrikaans word list from woorde.txt
 */

import strings from './strings'
import { dailyWordList, validWordList } from './woordeLys'

export interface IWordBankManager {
  loadWordBank(): Promise<Set<string>>
  getWordByIndex(index: number): string
  getWordCount(): number
  isValidWord(word: string): boolean
  retryLoadWordBank(): Promise<Set<string>>
  isUsingFallback(): boolean
  getLoadAttempts(): number
  setErrorNotificationCallback(callback: (message: string, isError: boolean) => void): void
}

export class WordBankManager implements IWordBankManager {
  private validWordSet: Set<string> = new Set() // For word validation (list A + B)
  private dailyWordArray: string[] = [] // For daily word selection (list A only)
  private isLoaded: boolean = false
  private loadAttempts: number = 0
  private maxRetries: number = 3
  private retryDelay: number = 1000 // 1 second
  private usingFallback: boolean = false
  private errorNotificationCallback?: (message: string, isError: boolean) => void

  /**
   * Set callback for error notifications
   * @param callback - Function to call when errors occur
   */
  setErrorNotificationCallback(callback: (message: string, isError: boolean) => void): void {
    this.errorNotificationCallback = callback
  }

  /**
   * Get comprehensive fallback word list for when woorde.txt fails to load
   * @returns string[] - Array of fallback Afrikaans words
   */
  private getFallbackWordList(): string[] {
    // Extended fallback list with more 5-letter Afrikaans words
    return [
      'WOORD', 'SPEEL', 'TEKEN', 'BRIEF', 'GROEN', 'WATER', 'HUIS', 'BOEKE',
      'TAFEL', 'STOEL', 'DEURE', 'KAMER', 'TUINE', 'BLARE', 'GRASE', 'LIEFS',
      'MOOIS', 'GROOT', 'KLEIN', 'GOEDS', 'WARMS', 'KOUD', 'HARDS', 'VINNIG',
      'HOOGS', 'LANGS', 'KORTS', 'BREED', 'DIEPS', 'SWAAR', 'STERK', 'SWAKS',
      'ANDER', 'ALMAL', 'PARTY', 'BAIES', 'MEERS', 'EERSTE', 'LAASTE', 'VOLGENDE',
      'VORIGE', 'WARES', 'MENSE', 'VROUENS', 'KINDE', 'SEUNS', 'VADERS', 'MOEDERS',
      'BROERS', 'SUSTERS', 'VRIENDE', 'WERKE', 'SKOLE', 'STEDE', 'LANDE', 'LEWES',
      'LIEFDE', 'VREDE', 'GELUK', 'VREES', 'HOPES', 'DROME', 'REGTE', 'AANDE',
      'AANRY', 'AARDE', 'ADRES', 'AFDEK', 'AFGEE', 'AFLEI', 'AFLOS', 'AFRUK',
      'AFSAK', 'AFSET', 'AFSNY', 'AFTEL', 'AFVAL', 'AFVEE', 'AFVRA', 'AGENT',
      'AGTER', 'AKSIE', 'ALARM', 'ALBEI', 'ALBUM', 'ALDUS', 'ALLES', 'ALTYD',
      'AMPER', 'ANGEL', 'APART', 'APIES', 'APPEL', 'ARMES', 'ARTSE', 'ASEMS',
      'ASOOK', 'ASPEK', 'ATLAS', 'BAARD', 'BABAS', 'BAKKE', 'BASIS', 'BEDEK',
      'BEDEL', 'BEDRA', 'BEELD', 'BEGIN', 'BEHOU', 'BEKAF', 'BEKEN', 'BEKOM',
      'BEKYK', 'BELAS', 'BELET', 'BELIG', 'BEMAN', 'BENDE', 'BENUT', 'BEOOG',
      'BEREI', 'BERGE', 'BERIG', 'BESEF', 'BESEM', 'BESET', 'BESIG', 'BESIT',
      'BESTE', 'BETER', 'BETIG', 'BETON', 'BEURS', 'BEURT', 'BEVAT', 'BEVEL',
      'BEWUS', 'BEWYS', 'BINNE', 'BISAR', 'BLAAI', 'BLAAR', 'BLAAS', 'BLANK',
      'BLASE', 'BLEEK', 'BLEIK', 'BLIND', 'BLINK', 'BLITS', 'BLOED', 'BLOEI',
      'BLOOT', 'BLOTE', 'BOAAN', 'BOERE', 'BOETE', 'BOLLE', 'BOMME', 'BOORD',
      'BORDE', 'BORGE', 'BRAND', 'BREED', 'BREEK', 'BREIN', 'BREUK', 'BRING',
      'BROEI', 'BROEK', 'BROER', 'BRONS', 'BROOD', 'BRUID', 'BRUIN', 'BRUTO'
    ].filter(word => word.length === 5) // Ensure only 5-letter words
  }

  /**
   * Loads the Afrikaans word bank from the imported word lists
   * Converts all words to uppercase for consistency
   * @returns Promise<Set<string>> - Set of valid Afrikaans words for validation
   */
  async loadWordBank(): Promise<Set<string>> {
    if (this.isLoaded) {
      return this.validWordSet
    }

    return this.loadWordBankWithRetry()
  }

  /**
   * Load word bank with retry mechanism
   * @returns Promise<Set<string>> - Set of valid Afrikaans words for validation
   */
  private async loadWordBankWithRetry(): Promise<Set<string>> {
    this.loadAttempts++

    try {
      // Load daily word list (list A only - for daily word selection)
      const dailyText = dailyWordList
      if (!dailyText || dailyText.trim().length === 0) {
        throw new Error('Daily word list is empty')
      }

      // Load valid word list (list A + B - for word validation)
      const validText = validWordList
      if (!validText || validText.trim().length === 0) {
        throw new Error('Valid word list is empty')
      }

      // Process daily word list (for daily word selection)
      const dailyLines = dailyText.split('\n')
      const dailyTrimmed = dailyLines.map((word: string) => word.trim())
      const dailyUppercase = dailyTrimmed.map((word: string) => word.toUpperCase())
      const dailyFiveLetters = dailyUppercase.filter((word: string) => word.length === 5)
      const dailyNonEmpty = dailyFiveLetters.filter((word: string) => word.length > 0)
      const dailyAlphabetic = dailyNonEmpty.filter((word: string) => /^[A-ZÀ-ÿ]+$/.test(word))

      // Process valid word list (for word validation)
      const validLines = validText.split('\n')
      const validTrimmed = validLines.map((word: string) => word.trim())
      const validUppercase = validTrimmed.map((word: string) => word.toUpperCase())
      const validFiveLetters = validUppercase.filter((word: string) => word.length === 5)
      const validNonEmpty = validFiveLetters.filter((word: string) => word.length > 0)
      const validAlphabetic = validNonEmpty.filter((word: string) => /^[A-ZÀ-ÿ]+$/.test(word))

      if (dailyAlphabetic.length === 0) {
        throw new Error('No valid 5-letter words found in daily word list')
      }

      if (validAlphabetic.length === 0) {
        throw new Error('No valid 5-letter words found in valid word list')
      }

      this.dailyWordArray = dailyAlphabetic
      this.validWordSet = new Set(validAlphabetic)
      this.isLoaded = true
      this.usingFallback = false

      // Successfully loaded Afrikaans words

      // Notify success if we had previous failures
      if (this.loadAttempts > 1 && this.errorNotificationCallback) {
        this.errorNotificationCallback(
          strings.wordBankLoadSuccess(validAlphabetic.length),
          false
        )
      }

      return this.validWordSet
    } catch (error) {
      console.error(`Word bank load attempt ${this.loadAttempts} failed:`, error)

      // If we can't load the imported file, use fallback
      const fallbackWords = this.getFallbackWordList()
      this.validWordSet = new Set(fallbackWords)
      this.dailyWordArray = fallbackWords
      this.isLoaded = true
      this.usingFallback = true

      if (this.errorNotificationCallback) {
        this.errorNotificationCallback(
          strings.wordBankUsingBackup(fallbackWords.length),
          true
        )
      }

      return this.validWordSet
    }
  }

  /**
   * Manually retry loading the word bank
   * Useful for user-initiated retry after failure
   * @returns Promise<Set<string>> - Set of valid Afrikaans words
   */
  async retryLoadWordBank(): Promise<Set<string>> {
    // Reset state for retry
    this.isLoaded = false
    this.loadAttempts = 0
    this.usingFallback = false
    this.validWordSet.clear()
    this.dailyWordArray = []

    return this.loadWordBank()
  }

  /**
   * Check if currently using fallback word list
   * @returns boolean - True if using fallback, false if using main word bank
   */
  isUsingFallback(): boolean {
    return this.usingFallback
  }

  /**
   * Get the number of load attempts made
   * @returns number - Number of attempts made to load word bank
   */
  getLoadAttempts(): number {
    return this.loadAttempts
  }

  /**
   * Gets a word by its index in the daily word array
   * Used for deterministic daily word selection (only from list A)
   * @param index - The index of the word to retrieve
   * @returns string - The word at the specified index
   */
  getWordByIndex(index: number): string {
    if (!this.isLoaded) {
      throw new Error('Word bank not loaded. Call loadWordBank() first.')
    }

    if (index < 0 || index >= this.dailyWordArray.length) {
      throw new Error(`Index ${index} out of bounds. Daily word list has ${this.dailyWordArray.length} words.`)
    }

    return this.dailyWordArray[index]
  }

  /**
   * Gets the total number of words available for daily word selection
   * @returns number - Total daily word count (list A only)
   */
  getWordCount(): number {
    if (!this.isLoaded) {
      throw new Error('Word bank not loaded. Call loadWordBank() first.')
    }

    return this.dailyWordArray.length
  }

  /**
   * Validates if a word exists in the valid word list
   * Used for checking user guesses (includes both list A and list B)
   * @param word - The word to validate (case-insensitive)
   * @returns boolean - True if word is valid, false otherwise
   */
  isValidWord(word: string): boolean {
    if (!this.isLoaded) {
      throw new Error('Word bank not loaded. Call loadWordBank() first.')
    }

    return this.validWordSet.has(word.toUpperCase())
  }
}

// Singleton instance for global use
export const wordBankManager = new WordBankManager()

/**
 * Utility function to load the Afrikaans word bank
 * Replaces the existing generateAcceptableWordSet function
 * @returns Promise<{wordSet: Set<string>}> - Compatible with existing code structure
 */
export async function generateAfrikaansWordSet(): Promise<{ wordSet: Set<string> }> {
  const wordSet = await wordBankManager.loadWordBank()
  return { wordSet }
}